import React, { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { AlertGroupName, useAlertState } from '../../hooks/useAlertState/useAlertState';
import { groupByVariant } from '../AlertProvider/alertParser';
import { INoticebarProps, Noticebar } from '../Noticebar/Noticebar';
import { BaseDiv } from '../../common/BaseElements';

const Container = styled(BaseDiv)`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const FixedContainer = styled(BaseDiv)<{ position: 'top' | 'bottom' }>`
  width: 100%;
  z-index: 100;
  ${({ position }) =>
    position === 'top' &&
    css`
      position: sticky;
      top: 0;
    `};
  ${({ position }) =>
    position === 'bottom' &&
    css`
      position: fixed;
      bottom: 0;
    `};
`;

export const NoticebarProvider: React.FC<PropsWithChildren> = ({ children, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'NoticebarProvider');
  const { state } = useAlertState(AlertGroupName.Notification);
  const noticeBarGroups = state && (groupByVariant(state) as INoticebarProps[][]);
  const top = noticeBarGroups?.map((alertGroup) => alertGroup.filter((n) => !n.position || (n.position === 'top' && !n.fixed)));
  const topFixed = noticeBarGroups?.map((alertGroup) => alertGroup.filter((n) => n.position === 'top' && n.fixed));
  const bottom = noticeBarGroups?.map((alertGroup) => alertGroup.filter((n) => n.position === 'bottom' && !n.fixed));
  const bottomFixed = noticeBarGroups?.map((alertGroup) => alertGroup.filter((n) => n.position === 'bottom' && n.fixed));

  return (
    <Container {...generateTestId()} {...rest}>
      {topFixed?.length > 0 && (
        <FixedContainer {...generateTestId('Top.Fixed')} position='top'>
          {topFixed?.map((notices) => notices.map((notice) => <Noticebar key={notice.id} {...notice} />))}
        </FixedContainer>
      )}
      {top?.map((notices) => notices.map((notice) => <Noticebar key={notice.id} {...notice} />))}
      <>{children}</>
      {bottom?.map((notices) => notices.map((notice) => <Noticebar key={notice.id} {...notice} />))}
      {bottomFixed?.length > 0 && (
        <FixedContainer {...generateTestId('Bottom.Fixed')} position='bottom'>
          {bottomFixed?.map((notices) => notices.map((notice) => <Noticebar key={notice.id} {...notice} />))}
        </FixedContainer>
      )}
    </Container>
  );
};
